import { Skeleton } from "@organizacaox/lai-design-system";
import { Play } from "@organizacaox/lai-design-system/icons";

export function CoursePlayerSkeleton({
  withPlaylist = true,
  label = "Carregando aula…",
}: {
  withPlaylist?: boolean;
  label?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-course-player-skeleton={withPlaylist ? "playlist" : "standalone"}
      className="w-full min-w-0 motion-reduce:[&_[data-slot=skeleton]]:animate-none"
    >
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className="flex min-w-0 flex-col gap-5">
        <div className="flex min-h-5 items-center justify-between gap-6">
          <Skeleton className="h-4 w-44 max-w-[60%]" />
          <Skeleton className="h-3 w-16 shrink-0" />
        </div>
        <div
          className={`grid min-w-0 items-start gap-6 ${withPlaylist ? "xl:grid-cols-[minmax(0,1fr)_20rem]" : ""}`}
        >
          <div className="relative aspect-video min-w-0 overflow-hidden rounded-xl border border-border bg-muted/40 xl:col-start-1">
            <Skeleton className="absolute inset-0 h-full w-full rounded-none opacity-50" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="grid size-14 place-items-center rounded-full border border-border/60 bg-background/60 sm:size-18">
                <Play className="size-5 text-muted-foreground/40 sm:size-7" />
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 space-y-3 border-t border-border/40 bg-background/60 px-3 py-3 sm:px-5 sm:py-4">
              <Skeleton className="h-1 w-full rounded-full" />
              <div className="flex items-center gap-3 sm:gap-4">
                <Skeleton className="size-3 rounded-sm" />
                <Skeleton className="h-2 w-12 rounded-full sm:w-20" />
                <Skeleton className="ml-auto size-3 rounded-sm" />
                <Skeleton className="size-3 rounded-sm" />
              </div>
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-5 xl:col-start-1">
            <div className="space-y-3">
              <Skeleton className="h-7 w-4/5 max-w-lg sm:h-8" />
              <Skeleton className="h-4 w-full max-w-2xl" />
              <Skeleton className="h-4 w-3/5 max-w-md" />
            </div>
            <div className="flex items-center justify-between border-t border-border pt-4">
              <Skeleton className="h-9 w-24 rounded-lg" />
              <Skeleton className="h-9 w-24 rounded-lg" />
            </div>
          </div>
          {withPlaylist && (
            <div className="min-w-0 overflow-hidden rounded-xl border border-border xl:col-start-2 xl:row-start-1 xl:row-span-3">
              <div className="space-y-3 border-b border-border bg-muted/40 p-4">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-5 w-4/5" />
              </div>
              <div className="space-y-5 p-3">
                {[0, 1].map((module) => (
                  <div key={module} className="space-y-2">
                    <Skeleton className="mx-1 my-3 h-3 w-2/3" />
                    {[0, 1, 2].map((lesson) => (
                      <div
                        key={lesson}
                        className={`flex items-center gap-3 rounded-lg p-2 ${module === 0 && lesson === 0 ? "bg-muted/50" : ""}`}
                      >
                        <Skeleton className="aspect-video w-16 shrink-0 rounded-md" />
                        <div className="flex min-w-0 flex-1 flex-col gap-2">
                          <Skeleton className={`h-3 ${lesson === 1 ? "w-4/5" : "w-full"}`} />
                          <Skeleton className="h-2 w-2/5" />
                        </div>
                        <Skeleton className="h-2 w-7 shrink-0" />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex min-w-0 flex-col gap-4 xl:col-start-1">
            <Skeleton className="h-5 w-36" />
            <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
              <Skeleton className="size-10 shrink-0 rounded-lg" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Skeleton className="h-3 w-3/5 max-w-64" />
                <Skeleton className="h-2 w-1/3 max-w-32" />
              </div>
              <Skeleton className="size-5 shrink-0 rounded-md" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
